import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import MuiAppBar from '@mui/material/AppBar';
import { styled as MuiStyle } from '@mui/material/styles';
import {
    Box,
    Typography,
    IconButton,
    Avatar,
    Menu,
    MenuItem,
    Tooltip,
} from '@mui/material';

import { 
    IconMenu2,
    IconSun,
    IconMoon,
} from '@tabler/icons-react';

import {
    ToolbarContainer,
} from '../../../Styles/PageLayout/rootLayoutStyles';

import { useAdminUserHook } from '../../../Hooks/AdminUserControl/adminUserControlHooks';

import { AppBarProps, HeaderNavigationInterface } from '../../../Models/PageLayout/HeaderNavigation';
import { Authentication } from "../../../Models/Authentication/Authentication";

import LocalStorageHelper from '../../../Utilities/Helpers/LocalStorageHelper';
import GlobalFunctionHelper from '../../../Utilities/Helpers/GlobalFunctionHelper';
import QueryBuilderHelper from '../../../Utilities/Helpers/QueryBuilderHelper';
import AccessibilityHelper from '../../../Utilities/Helpers/AccessibilityHelper';

import { 
    AuthenticationField,
    AdminUserField,
    AdminUserProfileField,
    RolesField,
} from "../../../Utilities/Enums/ParametersEnum";
import { CurrencyEnum } from '../../../Utilities/Enums/CurrencyEnums';
import { CachingEnum } from "../../../Utilities/Enums/CachingEnum";
import { RouteEnum } from '../../../Utilities/Enums/RouteEnums';
import { AdminModules } from '../../../Utilities/Enums/AdminModulesEnum';
import { ViewType } from '../../../Utilities/Enums/ViewTypeEnum';
import { RoleTypeEnum } from '../../../Utilities/Enums/RolesEnums';

import { GlobalConstants } from '../../../Constants/GlobalConstants';
import { StaticDisplayText } from '../../../Constants/StaticDisplayTexts';
import { AssetImages } from '../../../Constants/Assets';

const AppBar = MuiStyle(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme }) => ({
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    // background: `linear-gradient(90deg,rgba(7, 83, 171, 1) 0%, rgba(50, 131, 179, 1) 50%, rgba(133, 220, 255, 1) 100%)`,
    background: `#0753ab`,
    variants: [
        {
            props: ({ open }) => open,
            style: {
                width: `calc(100% - ${(GlobalConstants?.DRAWER_WIDTH ?? 240)}px)`,
                marginLeft: `${(GlobalConstants?.DRAWER_WIDTH ?? 240)}px`,
                transition: theme.transitions.create(['margin', 'width'], {
                    easing: theme.transitions.easing.easeOut,
                    duration: theme.transitions.duration.enteringScreen,
                }),
            },
        },
    ],
}));

const darkModeBroadcast = new BroadcastChannel("color-scheme");

export default function HeaderNavigation(props: HeaderNavigationInterface) {
    const navigate = useNavigate();
    const isDarkMode = LocalStorageHelper.isDarkMode();

	let {
        openDrawer,
        setOpenDrawer,
        handleOpenUserMenu,
        anchorElUser,
        setAnchorElUser,
    } = props;
    let userData: Authentication = LocalStorageHelper.getUserData();
    let isUpdateUserProfile: boolean = LocalStorageHelper?.isUpdateUserProfile() ?? false;
    let userMenus: any = AccessibilityHelper.hasAccess(AdminModules.MY_PROFILE_VIEW) ? 
        [
            {id: 1, title: 'Profile', navigateTo: `${RouteEnum.MY_PROFILE}?id=${userData?.[AuthenticationField.PUBLIC_ID]}&view=${ViewType.PREVIEW}`},
            {id: 2, title: 'Logout', navigateTo: RouteEnum.LOGOUT},
        ] : [{id: 2, title: 'Logout', navigateTo: RouteEnum.LOGOUT}];

    //Use States
    const [userProfileImage, setuserProfileImage] = useState<any>(AssetImages.USER_DEFAULT_PIC);
    const [userCurrency, setuserCurrency] = useState<CurrencyEnum>(CurrencyEnum.PHP);

    const [queryStr, setQueryStr] = useState({
        pageSize: 10,
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
    });
    const q_queryKey = [CachingEnum.ADMIN_USERS, queryStr]
    const { getAdminUserProfileImageById, getAdminUserById } = useAdminUserHook(q_queryKey, queryStr, false);

    //Event Handlers
    const getUserProf = async () => {
        if(userData?.[AuthenticationField.PUBLIC_ID]) {
            let getAdminUserProfile: any = await getAdminUserProfileImageById(userData?.[AuthenticationField.PUBLIC_ID]);
            let getProfileData: any = await getAdminUserById(userData?.[AuthenticationField.PUBLIC_ID]);

            setuserProfileImage(getAdminUserProfile?.data ?? AssetImages.USER_DEFAULT_PIC);
            setuserCurrency(getProfileData?.data?.[AdminUserField.PROFILE]?.[AdminUserProfileField.CURRENCY_CODE] ?? CurrencyEnum.PHP);

            LocalStorageHelper.saveUserData({
                ...userData,
                [AuthenticationField.CURRENCY_CODE]: getProfileData?.data?.[AdminUserField.PROFILE]?.[AdminUserProfileField.CURRENCY_CODE] ?? CurrencyEnum.PHP,
                [AuthenticationField.ROLE_NAME]: getProfileData?.data?.[AdminUserField.ROLE]?.[RolesField.NAME] ?? null,
                [AuthenticationField.VENDORS]: getProfileData?.data?.[AdminUserField.PROFILE]?.[AdminUserProfileField.VENDORS] ?? [],
            });
            LocalStorageHelper.saveUpdateUserProfile(false);
        }
    };

    const handleDrawerOpen = () => {
        if(setOpenDrawer) {
            setOpenDrawer(true);
        }
    };

    const toggleDarkMode = () => {
        let isDarkMode = LocalStorageHelper.isDarkMode();
        const newValDMode = !isDarkMode;
        LocalStorageHelper.saveDarkMode(newValDMode);
        darkModeBroadcast.postMessage(newValDMode ? "dark" : "light");
    };

    // const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    //     setAnchorElUser(event.currentTarget);
    // };

    const handleCloseUserMenu = (userMenuId?: number, navigateTo?: any) => {
        if(setAnchorElUser) {
            setAnchorElUser(null);
        }

        if (userMenuId == 2) {
            LocalStorageHelper.clearLocalStorage();
            
            if(navigateTo != null && navigateTo != "") {
                navigate(navigateTo, { replace: true });
            }
        }
    };

    useEffect(() => {
        if(isUpdateUserProfile) {
            getUserProf();
        }
    }, [isUpdateUserProfile])

    useEffect(() => {
        getUserProf();
    }, [])

    //Rendering Functions
    return <AppBar position="fixed" open={openDrawer} className='appBar'>
    {/*return <AppBar open={openDrawer} className='appBar'>*/}
        <ToolbarContainer>
            <div className="leftSideHeaderContainer">
                <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    onClick={handleDrawerOpen}
                    edge="start"
                    sx={[
                        {
                            mr: 2,
                        },
                        { display: (openDrawer ? 'none': 'inline-flex') }
                    ]}
                >
                    <IconMenu2 />
                </IconButton>
                {/*<Typography variant="h6" noWrap component="div">{StaticDisplayText.COMPANY_NAME}</Typography>*/}
                {userData?.[AuthenticationField.ROLE_TYPE] == RoleTypeEnum.SUPER_USER && <Typography variant="h6" noWrap component="div">{StaticDisplayText.COMPANY_NAME}</Typography>}
                {((userData?.[AuthenticationField.ROLE_TYPE] == RoleTypeEnum.USER_ADMIN || userData?.[AuthenticationField.ROLE_TYPE] == RoleTypeEnum.VENDOR_ADMIN) && userData?.[AuthenticationField.COMPANY_NAME] != null && userData?.[AuthenticationField.COMPANY_NAME] != "") && <Typography variant="h6" noWrap component="div">{userData?.[AuthenticationField.COMPANY_NAME]}</Typography>}
                {/*{(userData?.[AuthenticationField.ROLE_TYPE] == RoleTypeEnum.VENDOR_ADMIN && userData?.[AuthenticationField.VENDOR_NAME] != null && userData?.[AuthenticationField.VENDOR_NAME] != "") && <Typography variant="h6" noWrap component="div">{userData?.[AuthenticationField.VENDOR_NAME]}</Typography>}*/}
                
            </div>
            <Box className="rightSideHeaderContainer">
                <Typography className="currencyCode">Currency: {GlobalFunctionHelper.getDisplayCurrencyCode()}</Typography>
                <Tooltip className="darkModeBtn" title={(isDarkMode ? "Light" : "Dark") + " Theme"}>
                    <IconButton onClick={toggleDarkMode} sx={{ p: 0 }}>
                        {isDarkMode ? <IconSun /> : <IconMoon color={'#fff'} />}
                    </IconButton>
                </Tooltip>
                <Tooltip className="settingsBtn" title="Open settings">
                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                        <Avatar alt={userData?.[AuthenticationField.NAME] ?? ""} src={userProfileImage} />
                        <div className="userNameContainer">
                            {((userData?.[AuthenticationField.NAME] != null && userData?.[AuthenticationField.NAME] != "") || (userData?.[AuthenticationField.UNAME] != null && userData?.[AuthenticationField.UNAME] != "")) && <Typography className="userNameText">{userData?.[AuthenticationField.NAME] != null && userData?.[AuthenticationField.NAME] != "" ? userData?.[AuthenticationField.NAME] : (userData?.[AuthenticationField.UNAME] ?? "--")}</Typography>}
                            {userData?.[AuthenticationField.ROLE_NAME] != null && userData?.[AuthenticationField.ROLE_NAME] != "" && <Typography className="roleNameText">{userData?.[AuthenticationField.ROLE_NAME]}</Typography>}
                        </div>
                    </IconButton>
                </Tooltip>
                <Menu
                    sx={{ mt: '45px' }}
                    id="menu-appbar"
                    anchorEl={anchorElUser}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    open={Boolean(anchorElUser)}
                    onClose={() => handleCloseUserMenu()}
                >
                    {userMenus?.map((setting: any, index: number) => (
                        <MenuItem key={setting?.title} onClick={() => { handleCloseUserMenu(setting.id, setting.navigateTo); LocalStorageHelper.saveUpdateUserProfile(true); }}>
                            {setting.id == 2 ?
                                <Typography color="primaryText" sx={{ textAlign: 'center' }}>{setting?.title ?? "--"}</Typography> : 
                                <Link style={{textDecoration: 'none'}} to={setting.navigateTo}>
                                    <Typography color="primaryText" sx={{ textAlign: 'center' }}>{setting?.title ?? "--"}</Typography>
                                </Link>
                            }
                        </MenuItem>
                    ))}
                </Menu>
            </Box>
        </ToolbarContainer>
    </AppBar>
}